/**
 * N.1 — Bilgi Notlarım
 * Kullanıcının kendi oluşturduğu notların listesi + yeni not oluşturma/düzenleme
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import RichEditor from '../components/RichEditor';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const tkn = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const DURUM_RENK: Record<string, string> = {
  taslak: '#888',
  onay_bekliyor: '#e67e22',
  onaylandi: '#27ae60',
  reddedildi: '#e74c3c',
};
const DURUM_AD: Record<string, string> = {
  taslak: 'Taslak',
  onay_bekliyor: 'Onay Bekliyor',
  onaylandi: 'Onaylandı',
  reddedildi: 'Reddedildi',
};

const SABIT_ALANLAR = [
  { key: 'birim',             label: 'Birim',                  tip: 'text',     zorunlu: true },
  { key: 'konu',              label: 'Konu',                   tip: 'text',     zorunlu: true },
  { key: 'sunulan_makam',     label: 'Sunulan Makam',          tip: 'text',     zorunlu: false },
  { key: 'hazirlayan',        label: 'Hazırlayan',             tip: 'text',     zorunlu: true },
  { key: 'tarih',             label: 'Bilgi Notu Tarihi',      tip: 'date',     zorunlu: true },
  { key: 'yonetici_ozeti',    label: 'Yönetici Özeti',         tip: 'richtext', zorunlu: true },
  { key: 'ana_metin',         label: 'Ana Metin',              tip: 'richtext', zorunlu: false },
  { key: 'istatistiki_bilgi', label: 'İstatistiki Bilgiler',   tip: 'richtext', zorunlu: false },
  { key: 'sonuc',             label: 'Sonuç ve Değerlendirme', tip: 'richtext', zorunlu: false },
];

const emptyAlanlar = () => Object.fromEntries(SABIT_ALANLAR.map(a => [a.key, '']));

export default function BilgiNotListesi() {
  const userStr = localStorage.getItem('user');
  const user = userStr ? JSON.parse(userStr) : null;

  const [notlar, setNotlar] = useState<any[]>([]);
  const [sablonlar, setSablonlar] = useState<any[]>([]);
  const [onayAdaylari, setOnayAdaylari] = useState<any[]>([]);
  const [orgBirimler, setOrgBirimler] = useState<any[]>([]);

  const [mod, setMod] = useState<'liste' | 'form' | 'detay' | 'onay_sec'>('liste');
  const [secili, setSecili] = useState<any>(null);
  const [form, setForm] = useState<any>({ sablon_id: '', alanlar: emptyAlanlar(), etiketler: [] });
  const [seciliOnaylayan, setSeciliOnaylayan] = useState('');
  const [akis, setAkis] = useState<any[]>([]);

  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState('');
  const [mesaj, setMesaj] = useState('');

  const yukle = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/bilgi-notu/`, { headers: tkn(), params: { goruntule: 'benim' } });
      setNotlar(r.data);
    } catch { setNotlar([]); }
  }, []);

  useEffect(() => {
    yukle();
    axios.get(`${API}/api/bilgi-notu/sablonlar`, { headers: tkn() }).then(r => setSablonlar(r.data)).catch(() => {});
    // Org yapısındaki ana birimleri yükle (isletme tipi hariç)
    axios.get(`${API}/api/org/birimler-listesi`, { headers: tkn() })
      .then(r => setOrgBirimler(r.data.filter((b: any) => b.tur !== 'isletme')))
      .catch(() => {});
  }, [yukle]);

  const yeniAc = () => {
    const birimAd = user?.birim_adi || '';
    const hazAd = user?.ad_soyad || '';
    setSecili(null);
    setForm({
      sablon_id: sablonlar[0]?.id || '',
      alanlar: { ...emptyAlanlar(), birim: birimAd, hazirlayan: hazAd, tarih: new Date().toISOString().slice(0, 10) },
      etiketler: [],
    });
    setHata(''); setMesaj(''); setMod('form');
  };

  const duzenleAc = async (n: any) => {
    setSecili(n);
    setForm({ sablon_id: n.sablon_id || '', alanlar: { ...emptyAlanlar(), ...n.alanlar }, etiketler: n.etiketler || [] });
    try {
      const r = await axios.get(`${API}/api/bilgi-notu/${n.id}/akis`, { headers: tkn() });
      setAkis(r.data);
    } catch { setAkis([]); }
    setHata(''); setMesaj(''); setMod('form');
  };

  const kaydet = async (taslakOlarak = true) => {
    if (!form.alanlar.konu?.trim()) { setHata('Konu zorunludur'); return; }
    if (!form.alanlar.yonetici_ozeti?.trim()) { setHata('Yönetici Özeti zorunludur'); return; }
    setHata(''); setYukleniyor(true);
    try {
      if (secili) {
        await axios.put(`${API}/api/bilgi-notu/${secili.id}`, form, { headers: tkn() });
        setMesaj('Güncellendi');
      } else {
        await axios.post(`${API}/api/bilgi-notu/`, form, { headers: tkn() });
        setMesaj('Not oluşturuldu');
      }
      yukle(); setMod('liste');
    } catch (e: any) {
      setHata(e.response?.data?.detail || 'Hata');
    } finally {
      setYukleniyor(false);
    }
  };

  const onayaGonder = async () => {
    if (!seciliOnaylayan) { setHata('Onaylayan seçiniz'); return; }
    setYukleniyor(true); setHata('');
    try {
      const r = await axios.post(`${API}/api/bilgi-notu/${secili.id}/onaya-gonder`,
        { onaylayan_id: parseInt(seciliOnaylayan) }, { headers: tkn() });
      setMesaj(r.data.mesaj); yukle(); setMod('liste');
    } catch (e: any) {
      setHata(e.response?.data?.detail || 'Hata');
    } finally { setYukleniyor(false); }
  };

  const geriAl = async (n: any) => {
    if (!window.confirm('Notu geri almak istediğinizden emin misiniz?')) return;
    try {
      await axios.post(`${API}/api/bilgi-notu/${n.id}/geri-al`, {}, { headers: tkn() });
      setMesaj('Geri alındı'); yukle();
    } catch (e: any) { setHata(e.response?.data?.detail || 'Hata'); }
  };

  const sil = async (n: any) => {
    if (!window.confirm('Bu notu silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`${API}/api/bilgi-notu/${n.id}`, { headers: tkn() });
      setMesaj('Silindi'); yukle();
    } catch (e: any) { setHata(e.response?.data?.detail || 'Hata'); }
  };

  const alanDegistir = (key: string, val: string) => {
    setForm((f: any) => ({ ...f, alanlar: { ...f.alanlar, [key]: val } }));
  };

  const indir = (n: any, format: 'docx' | 'html') => {
    const token = localStorage.getItem('token');
    const url = `${API}/api/bilgi-notu/${n.id}/export?format=${format}`;
    if (format === 'docx') {
      fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => { if (!r.ok) throw new Error('İndirilemiyor'); return r.blob(); })
        .then(blob => {
          const a = document.createElement('a');
          a.href = URL.createObjectURL(blob);
          const konu = n.alanlar?.konu || `bilgi_notu_${n.id}`;
          a.download = konu.slice(0, 40).replace(/[^\w\s\-]/g, '').trim().replace(/\s+/g, '_') + '.docx';
          a.click();
          URL.revokeObjectURL(a.href);
        })
        .catch(e => alert('İndirme hatası: ' + e.message));
    } else {
      // HTML'i fetch ile çek, yeni pencerede aç → tarayıcı baskı iletişim kutusunu otomatik açar
      fetch(url, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => { if (!r.ok) throw new Error('İndirilemiyor'); return r.text(); })
        .then(html => {
          const win = window.open('', '_blank');
          if (win) { win.document.write(html); win.document.close(); }
        })
        .catch(e => alert('Yazdırma hatası: ' + e.message));
    }
  };

  // Dışa aktarım buton grubu
  const ExportButtons = ({ n, small = false }: { n: any; small?: boolean }) => {
    const s = small ? { fontSize: 12, padding: '3px 9px' } : { fontSize: 13, padding: '6px 14px' };
    return (
      <>
        <button
          onClick={() => indir(n, 'docx')}
          title="Word olarak indir"
          style={{ ...s, background: '#e8f4fd', color: '#1565c0', border: '1px solid #90caf9', borderRadius: 5, cursor: 'pointer', marginRight: 4 }}>
          ⬇ Word
        </button>
        <button
          onClick={() => indir(n, 'html')}
          title="Yazdır / PDF olarak kaydet"
          style={{ ...s, background: '#f3e5f5', color: '#6a1b9a', border: '1px solid #ce93d8', borderRadius: 5, cursor: 'pointer', marginRight: 4 }}>
          🖨️ PDF
        </button>
      </>
    );
  };

  const onaySecAc = async (n: any) => {
    setSecili(n);
    setSeciliOnaylayan('');
    setHata('');
    try {
      const r = await axios.get(`${API}/api/bilgi-notu/onay-adaylari`, { headers: tkn() });
      setOnayAdaylari(r.data);
    } catch { setOnayAdaylari([]); }
    setMod('onay_sec');
  };

  // İlgili Birim dropdown için filtre:
  // Daire başkanlıkları, GM, GMY, GM'ye bağlı birimler (denetim, yardımcı)
  // Şube müdürlükleri HARİÇ — Basın ve Halkla İlişkiler istisnası dahil
  const ilgiliBirimler = orgBirimler.filter((b: any) =>
    ['genel_mudur', 'gm_yardimcisi', 'daire', 'yardimci', 'denetim'].includes(b.tur) ||
    (b.tur === 'sube' && (b.ad || '').toLowerCase().includes('basın'))
  );

  // ── Liste görünümü ──────────────────────────────────────────
  if (mod === 'liste') return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, color: '#1a4a7a' }}>📝 Bilgi Notlarım</h2>
        <button onClick={yeniAc}
          style={{ background: '#1a4a7a', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', cursor: 'pointer', fontWeight: 600 }}>
          + Yeni Not
        </button>
      </div>

      {hata && <div style={{ background: '#fdecea', color: '#c0392b', padding: '8px 12px', borderRadius: 6, marginBottom: 12 }}>{hata}</div>}
      {mesaj && <div style={{ background: '#eafaf1', color: '#1e8449', padding: '8px 12px', borderRadius: 6, marginBottom: 12 }}>{mesaj}</div>}

      {notlar.length === 0 ? (
        <div style={{ textAlign: 'center', color: '#aaa', padding: 60, fontSize: 15 }}>
          Henüz bilgi notu oluşturmadınız.<br />
          <button onClick={yeniAc} style={{ marginTop: 16, background: '#1a4a7a', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 18px', cursor: 'pointer' }}>
            İlk Notumu Oluştur
          </button>
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f0f4f8', borderBottom: '2px solid #ddd' }}>
              {['Konu', 'Birim', 'Tarih', 'Onaylayan', 'Durum', 'İşlem'].map(h => (
                <th key={h} style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: '#444' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {notlar.map(n => (
              <tr key={n.id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '10px 12px', maxWidth: 260 }}>
                  <div style={{ fontWeight: 500 }}>{n.alanlar?.konu || '—'}</div>
                  {n.red_nedeni && (
                    <div style={{ color: '#e74c3c', fontSize: 12, marginTop: 2 }}>❌ {n.red_nedeni}</div>
                  )}
                </td>
                <td style={{ padding: '10px 12px', color: '#555' }}>{n.hazirlayan_birim || n.alanlar?.birim || '—'}</td>
                <td style={{ padding: '10px 12px', color: '#666', whiteSpace: 'nowrap' }}>
                  {n.alanlar?.tarih || n.olusturma_tarihi?.slice(0, 10) || '—'}
                </td>
                <td style={{ padding: '10px 12px', color: '#555' }}>{n.onaylayan_ad || '—'}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{
                    background: DURUM_RENK[n.durum] + '22', color: DURUM_RENK[n.durum],
                    padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                  }}>{DURUM_AD[n.durum] || n.durum}</span>
                </td>
                <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>
                  {n.durum === 'taslak' && (
                    <>
                      <button onClick={() => duzenleAc(n)}
                        style={{ background: '#eaf3fb', color: '#1a4a7a', border: '1px solid #bdd', borderRadius: 5, padding: '3px 10px', cursor: 'pointer', marginRight: 4, fontSize: 13 }}>
                        Düzenle
                      </button>
                      <button onClick={() => onaySecAc(n)}
                        style={{ background: '#eafaf1', color: '#1e8449', border: '1px solid #bde', borderRadius: 5, padding: '3px 10px', cursor: 'pointer', marginRight: 4, fontSize: 13 }}>
                        Onaya Gönder
                      </button>
                      <button onClick={() => sil(n)}
                        style={{ background: '#fdecea', color: '#c0392b', border: '1px solid #f5c6c6', borderRadius: 5, padding: '3px 8px', cursor: 'pointer', marginRight: 4, fontSize: 13 }}>
                        Sil
                      </button>
                    </>
                  )}
                  {n.durum === 'onay_bekliyor' && (
                    <button onClick={() => geriAl(n)}
                      style={{ background: '#fef9e7', color: '#b7950b', border: '1px solid #f9e47b', borderRadius: 5, padding: '3px 10px', cursor: 'pointer', marginRight: 4, fontSize: 13 }}>
                      Geri Al
                    </button>
                  )}
                  {n.durum === 'reddedildi' && (
                    <>
                      <button onClick={() => duzenleAc(n)}
                        style={{ background: '#eaf3fb', color: '#1a4a7a', border: '1px solid #bdd', borderRadius: 5, padding: '3px 10px', cursor: 'pointer', marginRight: 4, fontSize: 13 }}>
                        Düzenle
                      </button>
                      <button onClick={() => onaySecAc(n)}
                        style={{ background: '#eafaf1', color: '#1e8449', border: '1px solid #bde', borderRadius: 5, padding: '3px 10px', cursor: 'pointer', marginRight: 4, fontSize: 13 }}>
                        Tekrar Gönder
                      </button>
                    </>
                  )}
                  {n.durum === 'onaylandi' && (
                    <span style={{ color: '#27ae60', fontSize: 13, marginRight: 4 }}>✅ Onaylandı</span>
                  )}
                  <ExportButtons n={n} small />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );

  // ── Onaylayan seçme ekranı ──────────────────────────────────
  if (mod === 'onay_sec') return (
    <div style={{ padding: 24, maxWidth: 600, margin: '0 auto' }}>
      <button onClick={() => setMod('liste')} style={{ background: 'none', border: 'none', color: '#1a4a7a', cursor: 'pointer', fontSize: 14, marginBottom: 12 }}>
        ← Listeye Dön
      </button>
      <h3 style={{ color: '#1a4a7a', marginBottom: 4 }}>Onay Makamı Seçin</h3>
      <p style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>
        <strong>{secili?.alanlar?.konu}</strong> notu için onay makamı seçin
      </p>

      {hata && <div style={{ background: '#fdecea', color: '#c0392b', padding: '8px 12px', borderRadius: 6, marginBottom: 12 }}>{hata}</div>}

      {onayAdaylari.length === 0 ? (
        <div style={{ background: '#fff8e1', color: '#b7950b', padding: '12px 16px', borderRadius: 8, fontSize: 14 }}>
          ⚠️ Onay yapabilecek yetkili bulunamadı. Lütfen birim yöneticinize başvurun.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
            {onayAdaylari.map((a: any) => (
              <label key={a.kullanici_id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 10,
                padding: '12px 14px', border: `2px solid ${seciliOnaylayan === String(a.kullanici_id) ? '#1a4a7a' : '#ddd'}`,
                borderRadius: 8, cursor: 'pointer',
                background: seciliOnaylayan === String(a.kullanici_id) ? '#eaf3fb' : '#fff',
              }}>
                <input type="radio" name="onaylayan" value={a.kullanici_id}
                  checked={seciliOnaylayan === String(a.kullanici_id)}
                  onChange={() => setSeciliOnaylayan(String(a.kullanici_id))}
                  style={{ marginTop: 2 }} />
                <div>
                  <div style={{ fontWeight: 600, color: '#222' }}>{a.ad_soyad}</div>
                  <div style={{ fontSize: 12, color: '#777', marginTop: 2 }}>{a.org_birim_ad}</div>
                  {a.kategori?.startsWith('strateji') && (
                    <span style={{ fontSize: 11, background: '#e8f5e9', color: '#2e7d32', padding: '1px 6px', borderRadius: 8, marginTop: 3, display: 'inline-block' }}>
                      Strateji Geliştirme
                    </span>
                  )}
                  {a.kategori === 'kendi' && (
                    <span style={{ fontSize: 11, background: '#e3f2fd', color: '#1565c0', padding: '1px 6px', borderRadius: 8, marginTop: 3, display: 'inline-block' }}>
                      Kendi Onayı
                    </span>
                  )}
                </div>
              </label>
            ))}
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => setMod('liste')}
              style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: 6, background: '#f5f5f5', cursor: 'pointer', fontSize: 14 }}>
              İptal
            </button>
            <button onClick={onayaGonder} disabled={!seciliOnaylayan || yukleniyor}
              style={{ flex: 2, padding: '10px', background: !seciliOnaylayan ? '#ccc' : '#1a4a7a', color: '#fff', border: 'none', borderRadius: 6, cursor: seciliOnaylayan ? 'pointer' : 'not-allowed', fontWeight: 600, fontSize: 14 }}>
              {yukleniyor ? 'Gönderiliyor...' : 'Onaya Gönder'}
            </button>
          </div>
        </>
      )}
    </div>
  );

  // ── Form (yeni / düzenle) ────────────────────────────────────
  const aktifSablon = sablonlar.find((s: any) => String(s.id) === String(form.sablon_id));
  const alanSirasi = aktifSablon?.alan_sirasi || SABIT_ALANLAR.map(a => a.key);
  const aktifAlanlar = SABIT_ALANLAR.filter(a => alanSirasi.includes(a.key))
    .sort((a, b) => alanSirasi.indexOf(a.key) - alanSirasi.indexOf(b.key));

  return (
    <div style={{ padding: 24, maxWidth: 860, margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <button onClick={() => setMod('liste')} style={{ background: 'none', border: 'none', color: '#1a4a7a', cursor: 'pointer', fontSize: 14, marginBottom: 4, display: 'block' }}>
            ← Listeye Dön
          </button>
          <h2 style={{ margin: 0, color: '#1a4a7a' }}>
            {secili ? 'Bilgi Notunu Düzenle' : 'Yeni Bilgi Notu'}
          </h2>
        </div>
        {secili && (
          <div style={{ display: 'flex', gap: 8, paddingTop: 28 }}>
            <ExportButtons n={secili} />
          </div>
        )}
      </div>

      {hata && <div style={{ background: '#fdecea', color: '#c0392b', padding: '10px 14px', borderRadius: 6, marginBottom: 16 }}>{hata}</div>}
      {mesaj && <div style={{ background: '#eafaf1', color: '#1e8449', padding: '10px 14px', borderRadius: 6, marginBottom: 16 }}>{mesaj}</div>}

      {/* Şablon seçimi */}
      {sablonlar.length > 1 && (
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>Şablon</label>
          <select value={form.sablon_id} onChange={e => setForm((f: any) => ({ ...f, sablon_id: e.target.value }))}
            style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, width: '100%', maxWidth: 300 }}>
            {sablonlar.map((s: any) => <option key={s.id} value={s.id}>{s.ad}</option>)}
          </select>
        </div>
      )}

      {/* Alanlar */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
        {aktifAlanlar.map(alan => (
          <React.Fragment key={alan.key}>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#333', display: 'block', marginBottom: 6 }}>
                {alan.label}{alan.zorunlu && <span style={{ color: '#e74c3c' }}> *</span>}
              </label>

              {alan.key === 'birim' ? (
                <input
                  type="text"
                  value={form.alanlar[alan.key] || ''}
                  onChange={e => alanDegistir(alan.key, e.target.value)}
                  placeholder="Birim adını giriniz (ör. Strateji Geliştirme Dairesi Başkanlığı)"
                  disabled={!!(secili && secili.durum !== 'taslak' && secili.durum !== 'reddedildi')}
                  style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, width: '100%' }}
                />
              ) : alan.key === 'sunulan_makam' ? (
              <input
                type="text"
                value={form.alanlar[alan.key] || ''}
                onChange={e => alanDegistir(alan.key, e.target.value)}
                placeholder="Sunulan makamı giriniz (ör. Genel Müdür)"
                style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, width: '100%' }}
              />
            ) : alan.tip === 'richtext' ? (
              <RichEditor
                value={form.alanlar[alan.key] || ''}
                onChange={val => alanDegistir(alan.key, val)}
                placeholder={`${alan.label} giriniz...`}
                minHeight={alan.key === 'yonetici_ozeti' ? 140 : 100}
                disabled={secili && secili.durum !== 'taslak' && secili.durum !== 'reddedildi'}
              />
            ) : alan.tip === 'date' ? (
                <input type="date" value={form.alanlar[alan.key] || ''}
                  onChange={e => alanDegistir(alan.key, e.target.value)}
                  style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, width: 200 }} />
              ) : (
                <input type="text" value={form.alanlar[alan.key] || ''}
                  onChange={e => alanDegistir(alan.key, e.target.value)}
                  placeholder={`${alan.label} giriniz...`}
                  style={{ padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc', fontSize: 14, width: '100%' }} />
              )}
            </div>

            {/* İlgili Birim: birim'in hemen altında göster, belgede görünmez */}
            {alan.key === 'birim' && (
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#333', display: 'block', marginBottom: 6 }}>
                  İlgili Birim{' '}
                  <small style={{ fontSize: 11, color: '#888', fontWeight: 400 }}>— belgede görünmez</small>
                </label>
                <select
                  value={form.alanlar.ilgili_birim || ''}
                  onChange={e => alanDegistir('ilgili_birim', e.target.value)}
                  disabled={!!(secili && secili.durum !== 'taslak' && secili.durum !== 'reddedildi')}
                  style={{
                    padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc',
                    fontSize: 14, width: '100%',
                    background: (secili && secili.durum !== 'taslak' && secili.durum !== 'reddedildi') ? '#f5f5f5' : 'white',
                  }}>
                  <option value="">— Seçiniz —</option>
                  {ilgiliBirimler.map((b: any) => (
                    <option key={b.id} value={b.ad}>{b.ad}</option>
                  ))}
                </select>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Akış geçmişi */}
      {akis.length > 0 && (
        <div style={{ marginTop: 28, padding: 16, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e0e0e0' }}>
          <div style={{ fontWeight: 600, marginBottom: 10, color: '#555' }}>İşlem Geçmişi</div>
          {akis.map((a: any, i: number) => (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 6, fontSize: 13 }}>
              <span style={{ color: '#999', minWidth: 130 }}>{a.tarih?.slice(0, 16).replace('T', ' ')}</span>
              <span style={{ fontWeight: 500 }}>{a.yapan_ad}</span>
              <span style={{ color: '#666' }}>— {a.islem.replace(/_/g, ' ')}</span>
              {a.aciklama && <span style={{ color: '#e74c3c' }}>({a.aciklama})</span>}
            </div>
          ))}
        </div>
      )}

      {/* Butonlar */}
      {(!secili || secili.durum === 'taslak' || secili.durum === 'reddedildi') && (
        <div style={{ display: 'flex', gap: 12, marginTop: 28 }}>
          <button onClick={() => setMod('liste')}
            style={{ padding: '10px 24px', border: '1px solid #ddd', borderRadius: 6, background: '#f5f5f5', cursor: 'pointer', fontSize: 14 }}>
            İptal
          </button>
          <button onClick={() => kaydet()} disabled={yukleniyor}
            style={{ padding: '10px 24px', background: '#1a4a7a', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
            {yukleniyor ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
          {secili && (
            <button onClick={() => { setMod('liste'); setTimeout(() => onaySecAc(secili), 100); }}
              style={{ padding: '10px 24px', background: '#27ae60', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>
              Onaya Gönder
            </button>
          )}
        </div>
      )}
    </div>
  );
}
